import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { auth, db } from "../../../firebase";
import {
  doc, getDoc, setDoc, serverTimestamp, increment,
} from "firebase/firestore";

import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';

import DefaultAuth from 'layouts/auth/types/Default';
import illustration from 'assets/img/auth/auth.png';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { FcGoogle } from 'react-icons/fc';
import { getHomeByRole } from 'components/RoleRedirect';

// helper: traduz erros do Firebase para português curto
function firebaseMsg(error) {
  const map = {
    'auth/wrong-password':         'Senha incorreta.',
    'auth/invalid-credential':     'E-mail ou senha incorretos.',
    'auth/user-not-found':         'Usuário não encontrado.',
    'auth/invalid-email':          'E-mail inválido.',
    'auth/too-many-requests':      'Muitas tentativas. Aguarde.',
    'auth/user-disabled':          'Conta desativada.',
    'auth/network-request-failed': 'Sem conexão.',
    'auth/popup-blocked':          'Popup bloqueado pelo navegador.',
    'auth/email-already-in-use':   'E-mail já em uso.',
    'auth/account-exists-with-different-credential': 'Conta já existe com outro provedor.',
  };
  return map[error?.code] || 'Algo deu errado. Tente novamente.';
}

// helper: pega IP público com timeout e fallback
async function getPublicIP(timeoutMs = 3000) {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch("https://api.ipify.org?format=json", { signal: ctl.signal });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const { ip } = await r.json();
    return ip || null;
  } catch {
    return null; // fallback: segue sem IP
  } finally {
    clearTimeout(t);
  }
}

function SignIn() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorBrand = useColorModeValue('brand.500', 'white');
  const brandStars = useColorModeValue('brand.500', 'brand.400');
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [remember, setRemember] = React.useState(false);
  const [show, setShow] = React.useState(false);
  const [loadingGoogle, setLoadingGoogle] = React.useState(false);
  const handleClick = () => setShow(!show);

  React.useEffect(() => {
    if (location.state?.sessionExpired) {
      toast({
        title: 'Sessão expirada',
        description: 'Faça login novamente para continuar.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate, toast]);

  const handleGoogleSignIn = async () => {
    setLoadingGoogle(true);
    try {
      const { user } = await signInWithPopup(auth, new GoogleAuthProvider());

      const userRef = doc(db, "users", user.uid);
      const snap    = await getDoc(userRef);

      const baseData = {
        uid: user.uid,
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        providerId: "google.com",
      };

      if (!snap.exists()) {
        await setDoc(userRef, {
          ...baseData,
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          loginCount: 1,
        });
      } else {
        await setDoc(userRef, {
          ...baseData,
          lastLoginAt: serverTimestamp(),
          loginCount: increment(1),
        }, { merge: true });
      }

      const token = await user.getIdToken();
      sessionStorage.setItem("idToken", token);
      sessionStorage.setItem("userEmail", user.email ?? "");

      if (!snap.exists()) navigate("/auth/new-user");
      else navigate(getHomeByRole(snap.data()?.role));

      toast({ title: "Login com Google realizado!", status: "success" });
    } catch (error) {
      if (error.code !== "auth/popup-closed-by-user") {
        toast({ title: "Erro ao entrar com Google", description: firebaseMsg(error), status: "error" });
      }
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleSignIn = async () => {
    try {
      // 1) Persistência conforme "Lembrar de mim"
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);

      // 2) Login
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      // 3) Upsert do perfil no Firestore
      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);

      const baseData = {
        uid: user.uid,
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        providerId: user.providerData?.[0]?.providerId ?? "password",
      };

      if (!snap.exists()) {
        await setDoc(userRef, {
          ...baseData,
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          loginCount: 1,
        });
      } else {
        await setDoc(userRef, {
          ...baseData,
          lastLoginAt: serverTimestamp(),
          loginCount: increment(1),
        }, { merge: true });
      }

      // 4) Token e navegação
      const token = await user.getIdToken();
      sessionStorage.setItem("idToken", token);
      sessionStorage.setItem("userEmail", user.email ?? "");

      if (!snap.exists()) navigate("/auth/new-user");
      else navigate(getHomeByRole(snap.data()?.role));

      toast({ title: "Login feito com sucesso!", status: "success" });
    } catch (error) {
      toast({ title: "Erro no login", description: firebaseMsg(error), status: "error" });
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Entrar
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Acesse com seu e-mail e senha
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <FormControl>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired
              variant="auth"
              fontSize="sm"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@syncwave.net.br"
              mb="24px"
              fontWeight="500"
              size="lg"
            />
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              isRequired
              display="flex"
            >
              Senha<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup size="md">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isRequired
                fontSize="sm"
                placeholder="Mínimo de 8 caracteres"
                mb="24px"
                size="lg"
                type={show ? 'text' : 'password'}
                variant="auth"
              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: 'pointer' }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            <Flex justifyContent="space-between" align="center" mb="24px">
              <FormControl display="flex" alignItems="center">
                <Checkbox
                  id="remember-login"
                  colorScheme="brand"
                  me="10px"
                  isChecked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <FormLabel
                  htmlFor="remember-login"
                  mb="0"
                  fontWeight="normal"
                  color={textColor}
                  fontSize="sm"
                >
                  Lembrar de mim
                </FormLabel>
              </FormControl>
              <NavLink to="/auth/forgot-password/default">
                <Text
                  color={textColorBrand}
                  fontSize="sm"
                  w="124px"
                  fontWeight="500"
                >
                  Esqueceu a senha?
                </Text>
              </NavLink>
            </Flex>
            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="16px"
              onClick={handleSignIn}
            >
              Entrar
            </Button>

            {/* Divisor */}
            <Flex align="center" mb="16px">
              <Box flex="1" h="1px" bg="gray.200" />
              <Text mx="3" fontSize="sm" color={textColorSecondary} whiteSpace="nowrap">
                ou continue com
              </Text>
              <Box flex="1" h="1px" bg="gray.200" />
            </Flex>

            {/* Botão Google */}
            <Button
              w="100%"
              h="50"
              mb="24px"
              variant="outline"
              leftIcon={<Icon as={FcGoogle} boxSize="20px" />}
              onClick={handleGoogleSignIn}
              isLoading={loadingGoogle}
              loadingText="Aguarde..."
              fontSize="sm"
              fontWeight="500"
              borderColor="gray.200"
              _hover={{ bg: 'gray.50' }}
            >
              Entrar com Google
            </Button>
          </FormControl>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default SignIn;
